import type { ExperimentAssigner } from "@lib/analytics/experimentAssignments";

export type PurchaseOption =
  | "series"
  | "series,series2,series3"
  | "series2,series3-upgrade";

export const VALID_PURCHASE_OPTIONS: PurchaseOption[] = [
  "series",
  "series,series2,series3",
  "series2,series3-upgrade",
];

export const UPGRADE_PURCHASE_OPTION: PurchaseOption =
  "series2,series3-upgrade";

// Mirrors omg-api's isUpgradeOption (models/checkout.ts): a sale is an upgrade
// because of the SKU bought, not because the link carried `isUpgrade=true`.
// That query param also rides full-bundle links for signed-in members who own
// nothing, purely to skip step 1 (OMG-2717).
export const isUpgradeOption = (option: string | undefined): boolean =>
  option === UPGRADE_PURCHASE_OPTION;

export interface CheckoutRequest {
  email: string;
  password?: string;
  nonce: string;
  purchaseOption: PurchaseOption;
  quotedPrice: number;
  countryOverride?: string;
  currencyOverride?: string;
  discountCode?: string;
  isGift?: boolean;
  giftCount?: number;
  giftDeliveryType?: "print" | "send" | "printPdf";
  giftRecipientEmail?: string;
  giftRecipientName?: string;
  giftMessage?: string;
  fingerprint?: string;
  /**
   * Segment anonymousId from the FE's analytics-next instance. Sent so
   * server-side track() calls share the same anonymous identity, allowing
   * Mixpanel to merge the pre-identify FE events with the post-identify
   * user profile that the BE keys by Cognito sub.
   */
  anonymousId?: string;
  referralSource?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  landingPage?: string;
  gaClientId?: string;
  gaSessionId?: string;
  fbClickId?: string;
  fbBrowserId?: string;
  /**
   * The page the buyer submitted from (`window.location.href`). The BE
   * attaches it to the server-side Order Completed / purchase events as
   * Segment `context.page.url`, which the Meta CAPI destination maps to the
   * REQUIRED `event_source_url` (OMG-2538). Only send this to an omg-api that
   * accepts it (backend deploys first — tsoa rejects unknown fields).
   */
  pageUrl?: string;
  /**
   * PaymentFirst A/B experiment designator (Convert arm), sent from the
   * /checkout/pf route so the server can tag the purchase event per-arm
   * (OMG-2155). Names/semantics match the useAnalytics globals. Only send
   * these to an omg-api that accepts them (backend deploys first).
   */
  experiment?: string;
  experimentId?: string;
  variant?: string;
  variantId?: number;
  /**
   * Keyed experiment-assignment map (OMG-2490): `experiment_key -> arm`,
   * read from the first-party arm cookies (lib/analytics/
   * experimentAssignments.ts) and persisted server-side to
   * experiment_assignment — the revenue authority for A/B reads, unlike the
   * analytics-only pair above. Send-gated behind
   * VITE_SEND_EXPERIMENT_ASSIGNMENTS: only send to an omg-api that accepts
   * it (mono#462; backend deploys first — throw-on-extras).
   */
  experiments?: Record<string, string>;
  /** The bucketed visitor id (same gate) — links the purchase to the
   *  visitor-grain exposure rows. */
  experimentVisitorId?: string;
  /**
   * Which assigner minted the arm (OMG-2555): `edge` (paid splash funnel) or
   * `cms` (organic pricing page), read from the `omgyes_src_rg26` cookie the
   * minting assigner wrote beside the arm. Persisted as
   * experiment_assignment.metadata.assigner, the revenue read's only
   * paid/organic discriminator. Same send gate; the omg-api that accepts it
   * (mono#487) has been live on prod since the 10 Sep train.
   */
  experimentAssigner?: ExperimentAssigner;
  locale?: string;
  deviceData?: string;
  turnstileToken?: string;
  /**
   * One key per checkout attempt, stable across reload and rapid re-fires. The
   * server charges at most once per key (repeat of a completed key replays the
   * cached response; a concurrent in-flight duplicate returns
   * CHECKOUT_IN_PROGRESS). Optional — omitting it preserves the old behavior.
   */
  idempotencyKey?: string;
  /**
   * Version of the Terms of Service / Privacy Policy presented at purchase
   * (OMG-1929). The server persists it as the terms-acceptance record. Sourced
   * from VITE_TOS_VERSION and omitted while that var is unset - only set the
   * var for a stage once its omg-api accepts this field (deploy-order rule).
   */
  tosVersion?: string;
  // Set when PayPal is selected in a currency PayPal cannot settle in.
  // Server forces the charge currency to USD (routed to OMGYes_instant
  // merchant account) only when the resolved currency is genuinely in the
  // no-PayPal list, so the flag cannot be abused to change currency.
  paypalSwap?: boolean;
}

export const CheckoutError = {
  ALREADY_OWNS_PRODUCT: "ALREADY_OWNS_PRODUCT",
  ALREADY_OWNS_ALL: "ALREADY_OWNS_ALL",
  CUSTOMER_EXISTS: "CUSTOMER_EXISTS",
  EMAIL_NOT_RECOGNIZED: "EMAIL_NOT_RECOGNIZED",
  CHECKOUT_IN_PROGRESS: "CHECKOUT_IN_PROGRESS",
  // Pre-charge rejections whose BE `message` is purpose-written for the
  // buyer (see fgsake-monorepo CheckoutMessages) and must be shown as-is.
  INVALID_INPUT: "INVALID_INPUT",
  TURNSTILE_FAILED: "TURNSTILE_FAILED",
  // Both rate-limit shapes: the failure-count IP/fingerprint limiter and the
  // OMG-2124 per-email gateway-attempt budget ("wait about 20 minutes").
  RATE_LIMITED: "RATE_LIMITED",
} as const;

export type CheckoutErrorCode =
  (typeof CheckoutError)[keyof typeof CheckoutError];

/**
 * Error body the checkout endpoint attaches to a rejected attempt — the
 * shape read off `error.response.data` in BraintreeCheckout. All fields are
 * optional: older backends (and non-checkout failures surfacing through the
 * same axios error) may omit any of them.
 */
export interface CheckoutErrorPayload {
  name?: string;
  message?: string;
  /**
   * CUSTOMER_EXISTS only (OMG-2114): the existing account's entitlements are
   * strictly partial, so the upgrade path applies. Old backends omit it;
   * owns-all is excluded server-side.
   */
  upgradeable?: boolean;
}

export interface CheckoutResponse {
  success: boolean;
  transactionId?: string;
  customerId?: string;
  errorCode?: string;
  message?: string;
}
